// Invite-only access gate (pre-launch beta).
//
// InviteLayer wraps the whole app. When VITE_INVITE_ONLY is on, a fresh visitor
// with no session and no prior pass sees InviteGate — a full-screen code screen
// that unlocks login/signup once they present a valid invite code. The code is
// stashed and redeemed (tied to their account) once they sign up + verify their
// email. Anyone with a live session bypasses the gate (they're already through;
// the backend still enforces invite_required for the non-invited).
//
// Removed at launch: set VITE_INVITE_ONLY=off (rebuild) + INVITE_ONLY=off
// (backend). Then InviteLayer becomes a passthrough and the endpoints are inert.

import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { checkInvite, redeemInvite } from '@/lib/api';
import { Logo } from './Logo';
import { MeshBg } from './MeshBg';

const INVITE_ONLY = ['1', 'true', 'on', 'yes'].includes(
  String(import.meta.env.VITE_INVITE_ONLY ?? '').toLowerCase(),
);

const GATE_FLAG = 'invite_gate_ok';
const CODE_KEY = 'invite_code';

function readFlag(): boolean {
  try {
    return localStorage.getItem(GATE_FLAG) === '1';
  } catch {
    return false;
  }
}

function InviteGate({ authed, onPass }: { authed: boolean; onPass: () => void }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      setError('Enter your invite code.');
      return;
    }
    setBusy(true);
    const valid = await checkInvite(trimmed);
    if (!valid) {
      setBusy(false);
      setError("That invite code isn't valid. Double-check it and try again.");
      return;
    }
    // Remember the code + mark the gate passed. The code is redeemed after the
    // user signs up + verifies (see InviteLayer's effect) — or right now if
    // they're already signed in (returning user / bounced by invite_required).
    try {
      localStorage.setItem(CODE_KEY, trimmed);
      localStorage.setItem(GATE_FLAG, '1');
    } catch {
      // private-mode / storage blocked — the in-memory pass below still works
    }
    if (authed) {
      const r = await redeemInvite(trimmed);
      if (!r.ok && r.reason === 'invalid_or_used') {
        // Code exists but can't grant THIS user access (already claimed).
        try {
          localStorage.removeItem(GATE_FLAG);
          localStorage.removeItem(CODE_KEY);
        } catch {
          // ignore
        }
        setBusy(false);
        setError('That code has already been used. Ask for a fresh invite.');
        return;
      }
    }
    setBusy(false);
    onPass();
    // A fresh (unauthenticated) visitor who just unlocked the gate goes
    // straight to the login screen — not the marketing home page. Authed
    // users (bounced by invite_required) proceed to their destination, and
    // someone already on an /auth/* page stays put.
    if (!authed && !window.location.pathname.startsWith('/auth/')) {
      window.location.assign('/auth/sign-in');
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-surface flex items-center justify-center p-4">
      <MeshBg />
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3">
            <Logo height={73} />
          </div>
          <div className="flex justify-center mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
              <i className="ri-lock-2-line" />
              Private beta
            </span>
          </div>
          <p className="text-dark-muted text-sm">
            We&apos;re in a closed beta. Enter your invite code to continue.
          </p>
        </div>

        <div className="gradient-stroke bg-white rounded-2xl p-8 shadow-2xl shadow-primary/45">
          {error && (
            <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm font-medium border border-red-200">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label htmlFor="invite-code" className="block text-sm font-semibold text-dark mb-2">
                Invite code
              </label>
              <input
                id="invite-code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="XXXX-XXXX-XXXX"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                required
                className="w-full px-4 py-3 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm tracking-widest uppercase placeholder:text-dark-muted/50 placeholder:tracking-normal focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy ? (
                <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              ) : (
                <>
                  <i className="ri-arrow-right-line" />
                  Continue
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-dark-muted mt-6">
          Don&apos;t have a code?{' '}
          <a
            href="mailto:hello@adastronaut.com?subject=Beta%20invite%20request"
            className="font-semibold text-primary hover:text-primary-hover transition-colors"
          >
            Request access
          </a>
        </p>
      </div>
    </div>
  );
}

export function InviteLayer({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const [gateOk, setGateOk] = useState<boolean>(() => readFlag());
  const [blocked, setBlocked] = useState<boolean>(false);
  // Session-scoped redeem coordination (refs, not state, so the event listener's
  // closure always reads the latest value). `redeemedRef` = a redeem has
  // SUCCEEDED this session → the user IS invited, so any further invite_required
  // event is a stale race and must NOT bounce them. `redeemingRef` = a redeem is
  // in flight → don't fire a second concurrent one. Together these fix the
  // Google-signup bounce: Google users are instantly email-verified, so several
  // /api/* calls fire in parallel and each 403s invite_required before/around
  // the single redeem — the first redeem removed the code, and the stragglers
  // found no code and bounced an already-invited user back to the gate.
  const redeemedRef = useRef(false);
  const redeemingRef = useRef(false);

  // Backend said this session isn't invited. Usually that just means the redeem
  // hasn't landed yet (parallel calls race ahead of it) — so try to redeem the
  // stashed code and STAY in the app; only fall back to the gate if there's no
  // code AND we never redeemed, or the code is genuinely invalid.
  useEffect(() => {
    if (!INVITE_ONLY) return;
    const onRequired = () => {
      // Already invited this session — stale straggler event; ignore it.
      if (redeemedRef.current) return;
      // A redeem is already settling — let it resolve; don't double-fire.
      if (redeemingRef.current) return;
      let code: string | null = null;
      try { code = localStorage.getItem(CODE_KEY); } catch { code = null; }
      if (!code) {
        setBlocked(true);
        setGateOk(false);
        return;
      }
      redeemingRef.current = true;
      redeemInvite(code).then((r) => {
        redeemingRef.current = false;
        if (r.ok) {
          redeemedRef.current = true;
          try { localStorage.removeItem(CODE_KEY); } catch { /* ignore */ }
          setBlocked(false);
          // Tell brand-scoped hooks (useBrand) to re-fetch — their parallel
          // /api/* calls may have already 403'd invite_required before this
          // redeem landed and stranded on an error screen.
          window.dispatchEvent(new Event('sp:invite-redeemed'));
        } else if (!redeemedRef.current) {
          // Genuine failure (bad/used code) and we've never successfully
          // redeemed — show the gate. A concurrent success would have set
          // redeemedRef, in which case this failed straggler is ignored.
          setBlocked(true);
          setGateOk(false);
        }
      });
    };
    window.addEventListener('sp:invite-required', onRequired);
    return () => window.removeEventListener('sp:invite-required', onRequired);
  }, []);

  // Redeem the stashed code as soon as the user is signed in. We deliberately
  // do NOT gate on the client's user.emailVerified — Firebase leaves it stale
  // after email-link verification, which used to leave the code un-redeemed
  // while the backend (fresh token) already treated the user as verified →
  // invite_required bounce. The backend still enforces verification: an
  // unverified token returns email_not_verified (r.ok=false), so the code just
  // stays for a later retry. Idempotent for an already-invited user.
  useEffect(() => {
    if (!INVITE_ONLY) return;
    if (loading || !user) return;
    // Coordinate with the onRequired path via the shared refs — only one redeem
    // runs, and once it succeeds every future event is treated as invited.
    if (redeemedRef.current || redeemingRef.current) return;
    let code: string | null = null;
    try {
      code = localStorage.getItem(CODE_KEY);
    } catch {
      code = null;
    }
    if (!code) return;
    redeemingRef.current = true;
    redeemInvite(code).then((r) => {
      redeemingRef.current = false;
      if (r.ok) {
        redeemedRef.current = true;
        try {
          localStorage.removeItem(CODE_KEY);
        } catch {
          // ignore
        }
        setBlocked(false);
        // See the onRequired handler above — re-fetch brand-scoped data now
        // that the user is confirmed invited.
        window.dispatchEvent(new Event('sp:invite-redeemed'));
      }
    });
  }, [user, loading]);

  if (!INVITE_ONLY) return <>{children}</>;

  // Public pages that MUST be reachable from ANY browser with no invite code or
  // session:
  //   • Email action links (verify / reset) — self-authenticate via a one-time
  //     oobCode; a bad code just shows "link expired", grants no app access.
  //   • Legal / policy pages — Shopify & Meta app-review reviewers must open
  //     these without an invite; they grant no app access on their own.
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const UNGATED_PATHS = [
    '/auth/verify-email',
    '/auth/reset-password',
    '/privacy',
    '/privacy-app',
    '/terms',
    '/data-deletion',
  ];
  if (UNGATED_PATHS.some((p) => path === p || path.startsWith(p + '/'))) {
    return <>{children}</>;
  }

  // Don't flash the gate at a returning signed-in user while auth resolves.
  if (loading && !gateOk) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-6 h-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  const passed = !blocked && (gateOk || !!user);
  if (passed) return <>{children}</>;

  return (
    <InviteGate
      authed={!!user}
      onPass={() => {
        setGateOk(true);
        setBlocked(false);
      }}
    />
  );
}
